import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField,
} from '@mui/material';
import axios from '../../../config/axiosInstance';
import { useSnackbar } from 'notistack';
import { WEEKDAYS, isWeekdayCellRule, errorMessage } from './staffingUtils';

const CELL_W = 76;

const inputSx = {
  width: 58,
  '& .MuiInputBase-input': { textAlign: 'center', padding: '4px 6px', fontSize: 13 },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};

const fmt = (v) => (v || '').slice(0, 5);
const fmtRange = (r) => (
  r.break_from && r.break_to
    ? `${fmt(r.hour_from)}–${fmt(r.break_from)} / ${fmt(r.break_to)}–${fmt(r.hour_to)}`
    : `${fmt(r.hour_from)}–${fmt(r.hour_to)}`
);

// Valor por defecto + un valor opcional por día de la semana. Una celda de día vacía
// hereda el valor por defecto (se muestra atenuado como referencia).
export default function StaffingMatrix({ stationId, area, workshifts, rules, defaultFor, canWrite, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const primaryWorkareaId = area.workareaIds[0];

  const weekdayRule = useMemo(() => {
    const map = {};
    (rules || []).forEach((r) => {
      if (r.workarea_id === primaryWorkareaId && isWeekdayCellRule(r)) {
        map[`${r.workshift_id}-${r.weekdays[0]}`] = r;
      }
    });
    return map;
  }, [rules, primaryWorkareaId]);

  const serverValues = useMemo(() => {
    const values = {};
    workshifts.forEach((w) => {
      values[`${w.id}-default`] = String(defaultFor(area, w.id));
      WEEKDAYS.forEach((d) => {
        const rule = weekdayRule[`${w.id}-${d.value}`];
        values[`${w.id}-${d.value}`] = rule ? String(rule.total) : '';
      });
    });
    return values;
  }, [workshifts, area, defaultFor, weekdayRule]);

  const [draft, setDraft] = useState(serverValues);
  useEffect(() => { setDraft(serverValues); }, [serverValues]);

  const save = async (workshiftId, column) => {
    const key = `${workshiftId}-${column}`;
    const raw = draft[key] ?? '';
    if (raw === serverValues[key]) return;

    try {
      let response;
      if (column === 'default') {
        if (raw === '') { setDraft((p) => ({ ...p, [key]: serverValues[key] })); return; }
        response = await axios.put(`/station/${stationId}/staffing/default`, {
          workarea_ids: area.workareaIds,
          workshift_id: workshiftId,
          total: Number(raw),
        });
      } else {
        response = await axios.put(`/station/${stationId}/staffing/weekday`, {
          workarea_id: primaryWorkareaId,
          workshift_id: workshiftId,
          weekday: column,
          total: raw === '' ? null : Number(raw),
        });
      }
      await onChanged(response?.data?.suggested_recalc);
    } catch (e) {
      setDraft((p) => ({ ...p, [key]: serverValues[key] }));
      enqueueSnackbar(errorMessage(e, 'No se pudo guardar la dotación.'), { variant: 'error' });
    }
  };

  const cell = (workshiftId, column) => {
    const key = `${workshiftId}-${column}`;
    const isDefault = column === 'default';
    return (
      <TableCell key={key} align="center" sx={{ minWidth: CELL_W, p: '4px', bgcolor: isDefault ? '#f5f8fb' : 'inherit' }}>
        <TextField
          type="number"
          size="small"
          value={draft[key] ?? ''}
          placeholder={isDefault ? '' : serverValues[`${workshiftId}-default`]}
          onChange={(e) => setDraft((p) => ({ ...p, [key]: e.target.value.replace(/[^\d]/g, '') }))}
          onBlur={() => save(workshiftId, column)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          inputProps={{ min: 0, 'aria-label': `Dotación ${isDefault ? 'por defecto' : WEEKDAYS[column].long}` }}
          disabled={!canWrite}
          sx={inputSx}
        />
      </TableCell>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Valor por defecto y, opcionalmente, uno distinto por día de la semana. Un día vacío usa el valor por defecto.
      </Typography>
      <Box sx={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Turno</TableCell>
              <TableCell align="center" sx={{ minWidth: CELL_W, bgcolor: '#f5f8fb', fontWeight: 700 }}>Por defecto</TableCell>
              {WEEKDAYS.map((d) => (
                <TableCell key={d.value} align="center" sx={{ minWidth: CELL_W }}>{d.short}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {workshifts.map((w) => (
              <TableRow key={w.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{w.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtRange(w)}</Typography>
                </TableCell>
                {cell(w.id, 'default')}
                {WEEKDAYS.map((d) => cell(w.id, d.value))}
              </TableRow>
            ))}
            {workshifts.length === 0 && (
              <TableRow><TableCell colSpan={9}>Esta estación no tiene turnos principales configurados.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
